import { get, post, patch, del } from './apiService';
import type {
  ReserveOrderRequest,
  ReserveOrderResponse,
  ConfirmOrderRequest,
  ConfirmOrderResponse,
  OrderDetail,
  CancelPreviewResponse,
  MyOrderSummary,
} from '../types/order';
import type { Payment } from '../types/payment';
import type { Address } from '../types/address';
import type { TimeWindow } from '../types/listing';

export async function reserveOrder(
  body: ReserveOrderRequest,
): Promise<ReserveOrderResponse> {
  return post<ReserveOrderResponse>('/orders/reserve', body);
}

export async function confirmOrder(
  id: string,
  body: ConfirmOrderRequest,
): Promise<ConfirmOrderResponse> {
  return post<ConfirmOrderResponse>(`/orders/${id}/confirm`, body);
}

export async function getOrderDetail(id: string, opts: { quiet404?: boolean } = {}): Promise<OrderDetail> {
  return get<OrderDetail>(`/orders/${id}`, undefined, opts);
}

export interface GetMyOrdersParams {
  /** Page size (server caps at 100). */
  fetchSize?: number;
  /** Cursor — return orders created strictly before this order id. */
  lastOrderId?: string;
  /** 'all' returns every status; 'paid' (default) hides never-paid orders. */
  include?: 'all' | 'paid';
}

/**
 * The caller's own placed orders (buyer-side history), newest first.
 * Cursor-paginated: pass the last row's orderId as `lastOrderId` to fetch the
 * next page.
 */
export async function getMyOrders(
  params: GetMyOrdersParams = {},
): Promise<MyOrderSummary[]> {
  const query = new URLSearchParams();
  if (params.fetchSize != null) query.set('fetchSize', String(params.fetchSize));
  if (params.lastOrderId) query.set('lastOrderId', params.lastOrderId);
  if (params.include) query.set('include', params.include);
  const qs = query.toString();
  return get<MyOrderSummary[]>(`/orders/mine${qs ? `?${qs}` : ''}`);
}

export interface ListingOrderSummary {
  orderId: string;
  orderNumber: string;
  status: string;
  quantity: number;
  fulfillmentMethod: string;
  /** Gross (price * qty) in cents — what the lister is made whole on. This, not
   *  `totalPaid`, is the figure shown on lister-facing order cards. Optional
   *  only for wire compatibility with a backend that predates the field. */
  subtotal?: number;
  /** Per-unit price in cents. Present on the order-detail shape; lets a card
   *  derive gross when `subtotal` is absent. */
  pricePerUnit?: number;
  /** The EATER's charge in cents — gross plus platform fees and tax. Never show
   *  this to the lister: none of the fee/tax portion reaches them. */
  totalPaid: number;
  createdAt: string;
  orderedFor?: {
    id: string;
    displayName: string;
    avatarUrl: string;
    businessName?: string;
    connectionLevel?: number | null;
  };
  lister?: {
    id: string;
    displayName: string;
    avatarUrl: string;
    businessName?: string;
    connectionLevel?: number | null;
  };
  intendedPickupTime?: string;
  /** Flat per-order delivery charge in cents. 0 unless this is a paid drop-off. */
  dropoffFee?: number;
  /** Drop-off only: the listing's drop-off window, so the card can state the
   *  range the run is due in rather than just its start. */
  dropoffWindow?: TimeWindow | null;
  /** Drop-off only: where the chef is driving. Withheld until the chef
   *  accepts; present from `accepted` on (and through `no_show`). */
  orderAddress?: Address | null;
  verificationCode?: number;
  // On-my-way state at handoff — the buyer has signalled they're heading over
  // for a pickup order. Lets the lister card show a "customer is on their way"
  // indicator, and hides the orderer's heads-up button once sent. Does not gate
  // the pickup ball; verificationCode is ungated at ready_to_pickup.
  onMyWay?: boolean;
  // Completion-handshake state at handoff (ready_to_pickup). Lets
  // the lister card render the three-state confirm language. `listerConfirmed`
  // also hides the lister's Remind button and the orderer's "On My Way" button:
  // both are pre-handoff prods with nothing left to prod once the food is gone.
  // Note it no longer affects verificationCode — that is ungated at handoff.
  ordererConfirmed?: boolean;
  listerConfirmed?: boolean;
  // Whether the lister has spent their one "come get it" nudge. Separate budget
  // from listerConfirmed — nudging doesn't consume the handoff confirm.
  listerNudged?: boolean;
  hasReviewed?: boolean;
  reviewEligible?: boolean;
  /** End of the parent listing's pickup window (RFC3339). Gates the lister's
   * "Mark as No-show" button — no-show isn't allowed until the window closes. */
  listingTimeWindowEnd?: string;
  // Payment summary (cents) for the post-cancel orderer view.
  amountTotal?: number;
  canceledBy?: 'lister' | 'eater' | 'system';
  amountCaptured?: number;
  amountRefunded?: number;
  amountFees?: number;
  /** Chef's gross earnings for this order (cents), from TransactionSnapshot.
   *  Absent on free-completed orders (no snapshot) and on in-flight rows
   *  that haven't reached a settlement yet — FE renders "Free" in that case
   *  for the completed-orders section on the chef listing page. */
  grossEarnedCents?: number;
}

export async function getOrdersForListing(
  listingId: string,
): Promise<ListingOrderSummary[]> {
  return get<ListingOrderSummary[]>(`/listings/${listingId}/orders`);
}

export async function getPaymentStatus(paymentId: string): Promise<Payment> {
  return get<Payment>(`/stripe/payment/${paymentId}`);
}

export async function cancelOrder(
  id: string,
  options?: { keepalive?: boolean },
): Promise<void> {
  return del<void>(`/orders/${id}`, undefined, options);
}

export async function getCancelPreview(
  id: string,
): Promise<CancelPreviewResponse> {
  return get<CancelPreviewResponse>(`/orders/${id}/cancel-preview`);
}

export interface VerifyOrderResponse {
  success: boolean;
  status?: string;
  refresh?: boolean;
  // Handshake state. When the orderer confirms first, `status` stays at the
  // handoff value (not 'completed') and these reflect that only the orderer has
  // confirmed so the UI can show "waiting on the chef".
  ordererConfirmed?: boolean;
  listerConfirmed?: boolean;
}

export async function verifyOrder(
  id: string,
  code: number,
): Promise<VerifyOrderResponse> {
  return post<VerifyOrderResponse>(`/orders/${id}/verify`, { code });
}

export interface ListerConfirmResponse {
  success: boolean;
  status?: string;
  ordererConfirmed?: boolean;
  listerConfirmed?: boolean;
}

/**
 * The lister's half of the completion handshake (no body). Completes the order
 * if the orderer has already confirmed; otherwise records the lister's confirm
 * and pings the orderer once. See backend order.go listerConfirmHandler.
 */
export async function listerConfirmOrder(
  id: string,
): Promise<ListerConfirmResponse> {
  return post<ListerConfirmResponse>(`/orders/${id}/lister-confirm`, {});
}

export interface OnMyWayResponse {
  success: boolean;
  status?: string;
  onMyWay?: boolean;
}

/**
 * The orderer signals they're on their way to pick up a ready_to_pickup order
 * (no body). Unlocks the verification code (withheld by the backend until now)
 * and notifies the lister. Idempotent server-side. See backend order.go
 * onMyWayHandler.
 */
export async function onMyWayOrder(id: string): Promise<OnMyWayResponse> {
  return post<OnMyWayResponse>(`/orders/${id}/on-my-way`, {});
}

export interface NudgeOrderResponse {
  success: boolean;
  status?: string;
  listerNudged?: boolean;
}

/**
 * The lister's "come get it" prod to the eater on a ready_to_pickup pickup order
 * (no body). Pure notification — it does NOT touch the completion handshake, so
 * the lister's separate handoff confirm stays available afterwards. One per order
 * server-side. See backend order.go nudgeHandler.
 */
export async function nudgeOrder(id: string): Promise<NudgeOrderResponse> {
  return post<NudgeOrderResponse>(`/orders/${id}/nudge`, {});
}

export interface DenyHandoffResponse {
  success: boolean;
  status?: string;
  listerConfirmed?: boolean;
  ordererConfirmed?: boolean;
}

/**
 * The orderer's "No, I did not get my dish" answer to the lister's Handed Off
 * (no body). Clears the lister's handoff confirm, putting the order back to its
 * pre-handed-off state, and notifies the lister. The eater's cancel stays locked.
 * Idempotent server-side. See backend order.go denyHandoffHandler.
 */
export async function denyHandoff(id: string): Promise<DenyHandoffResponse> {
  return post<DenyHandoffResponse>(`/orders/${id}/deny-handoff`, {});
}

export async function patchOrderStatus(
  id: string,
  status: string,
  rejectionReason?: string,
): Promise<{ orderId: string; status: string }> {
  return patch<{ orderId: string; status: string }>(`/orders/${id}/status`, {
    status,
    ...(rejectionReason ? { rejectionReason } : {}),
  });
}

export interface MarkAllOrdersReadyResponse {
  listingId: string;
  autoReadyEnabled: boolean;
  /** How many currently-accepted pickup orders were flipped to ready by this call. */
  ordersReadied: number;
}

/**
 * Arms (enabled=true) or disarms (enabled=false) auto-ready for a listing. When
 * arming, the backend also flips every currently-accepted pickup order to
 * ready_to_pickup, and every future accepted pickup order auto-flips to ready
 * while the pickup window is open. Disarming only clears the flag — orders
 * already marked ready are left as-is. Lister only. See backend
 * markAllOrdersReadyHandler.
 */
export async function markAllOrdersReady(
  listingId: string,
  enabled: boolean,
): Promise<MarkAllOrdersReadyResponse> {
  return post<MarkAllOrdersReadyResponse>(
    `/listings/${listingId}/orders/mark-all-ready`,
    { enabled },
  );
}

export interface StartDropoffRunResponse {
  listingId: string;
  /** How many accepted drop-off orders went out for delivery with this run. */
  ordersOnTheirWay: number;
}

/**
 * The chef is leaving the house: every accepted drop-off order on the listing
 * goes out for delivery and each eater is notified. Refused with
 * DROPOFF_RUN_PENDING_ORDERS (params.pendingCount) while any drop-off order is
 * still awaiting accept/decline. Lister only. See backend startDropoffRunHandler.
 */
export async function startDropoffRun(listingId: string): Promise<StartDropoffRunResponse> {
  return post<StartDropoffRunResponse>(`/listings/${listingId}/orders/start-dropoff-run`, {});
}

export interface CreateOrderReviewRequest {
  rating: number;
  note?: string;
}

export async function createOrderReview(
  orderId: string,
  body: CreateOrderReviewRequest,
): Promise<void> {
  return post<void>(`/orders/${orderId}/review`, body);
}
